"use client";

import * as Sentry from "@sentry/nextjs";
import dynamic from "next/dynamic";
import { Component, createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode, type Dispatch, type SetStateAction } from "react";
import { safeGet, safeRemove, safeSet } from "@/components/onboarding/safe-storage";
import { refusal } from "@/convex/refusals";
import type { Share } from "@/convex/shares";
import type { PlayRef } from "@/lib/community/play";
import { useAuth } from "@/contexts/auth-context";
import { useToast } from "@/contexts/toast-context";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useIsMobile } from "@/hooks/use-mobile";
import { isLocalBypassAuthEnabled, LOCAL_BYPASS_USER_ID } from "@/lib/local-bypass-auth";
import { phCaptureException } from "@/utils/analytics";

export type Unread = "message" | "mention" | null;
export type StagedPlay = { readonly ref: PlayRef; readonly preview: Share };
export type ProfileSlot = { readonly node: HTMLElement; readonly chatNode: HTMLElement; readonly handle: string };
export type Guest = "signed_out" | "no_plan" | null;
type Revoke = Exclude<Guest, null> | "failed";

const GUEST_SCOPE = "guest";

type ChatShell = {
  userId: string;
  guest: Guest;
  open: boolean;
  setOpen: (open: boolean) => void;
  unread: Unread;
  setUnread: (unread: Unread) => void;
  introSeen: boolean;
  refused: boolean;
  revoke: (reason: Revoke) => void;
  restore: () => void;
  pending: StagedPlay | null;
  setPending: Dispatch<SetStateAction<StagedPlay | null>>;
  share: (play: StagedPlay | null) => void;
  pageLeague: string | null;
  setPageLeague: (league: string | null) => void;
  pageSlot: HTMLElement | null;
  setPageSlot: (slot: HTMLElement | null) => void;
  profile: ProfileSlot | null;
  setProfile: (slot: ProfileSlot | null) => void;
  focusPage: () => void;
};

const ChatShellContext = createContext<ChatShell | null>(null);

export function chatStorageKey(userId: string, key: "open" | "last_seen_at" | "width" | "intro_seen") {
  return `upsider_chat:${userId}:${key}`;
}

export function useChatShell() {
  return useContext(ChatShellContext);
}

export function useChatPageLeague(league: string | null) {
  const setPageLeague = useChatShell()?.setPageLeague;
  const upper = league?.toUpperCase() ?? null;
  useEffect(() => {
    if (!setPageLeague) return;
    setPageLeague(upper);
    return () => setPageLeague(null);
  }, [setPageLeague, upper]);
}

function useChatUserId() {
  const { user, isLoading } = useAuth();
  const bypass = isLocalBypassAuthEnabled();
  const flagOn = useFeatureFlag("community-chat");
  const [revoked, setRevoked] = useState<{ userId: string; reason: Revoke } | null>(null);
  const signedIn = bypass ? LOCAL_BYPASS_USER_ID : (user?.id ?? null);
  const mine = revoked?.userId === signedIn ? revoked.reason : null;
  const enabled = Boolean(process.env.NEXT_PUBLIC_CONVEX_URL) && !(isLoading && !bypass) && (flagOn || signedIn === null) && mine !== "failed";
  const guest: Guest = signedIn === null ? "signed_out" : mine === "failed" ? null : mine;
  const revoke = useCallback((reason: Revoke) => {
    if (signedIn !== null) setRevoked({ userId: signedIn, reason });
  }, [signedIn]);
  const restore = useCallback(() => setRevoked(null), []);
  const refused = mine === "signed_out" || mine === "no_plan";
  return { userId: enabled ? (signedIn ?? GUEST_SCOPE) : null, guest, refused, revoke, restore };
}

export function ChatShellProvider({ children }: { children: ReactNode }) {
  const { userId, guest, refused, revoke, restore } = useChatUserId();
  const isMobile = useIsMobile();
  const [open, setOpenState] = useState(false);
  const [unread, setUnread] = useState<Unread>(null);
  const [pending, setPending] = useState<StagedPlay | null>(null);
  const [pageLeague, setPageLeague] = useState<string | null>(null);
  const [pageSlot, setPageSlot] = useState<HTMLElement | null>(null);
  const [profile, setProfile] = useState<ProfileSlot | null>(null);
  const [introSeen, setIntroSeen] = useState(true);

  useEffect(() => {
    setPending(null);
    setOpenState(userId !== null && !isMobile && safeGet(chatStorageKey(userId, "open")) === "1");
    setIntroSeen(!userId || safeGet(chatStorageKey(userId, "intro_seen")) === "1" || safeGet(chatStorageKey(userId, "last_seen_at")) !== null);
  }, [userId]);

  const reading = open || pageSlot !== null;
  useEffect(() => {
    if (!userId || !reading) return;
    safeSet(chatStorageKey(userId, "intro_seen"), "1");
    setIntroSeen(true);
  }, [userId, reading]);

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      if (!userId || isMobile) return;
      if (next) safeSet(chatStorageKey(userId, "open"), "1");
      else safeRemove(chatStorageKey(userId, "open"));
    },
    [userId, isMobile],
  );

  const { showToast } = useToast();
  const share = useCallback(
    (next: StagedPlay | null) => {
      if (next === null) {
        showToast(refusal("bad_share").message, "error");
        return;
      }
      setPending(next);
      setOpen(true);
    },
    [setOpen, showToast],
  );

  const focusPage = useCallback(() => pageSlot?.querySelector<HTMLInputElement>("form input")?.focus(), [pageSlot]);

  const value = useMemo(
    () =>
      userId
        ? { userId, guest, refused, open, setOpen, unread, setUnread, introSeen, revoke, restore, pending, setPending, share, pageLeague, setPageLeague, pageSlot, setPageSlot, profile, setProfile, focusPage }
        : null,
    [userId, guest, refused, open, setOpen, unread, introSeen, revoke, restore, pending, share, pageLeague, pageSlot, profile, focusPage],
  );
  return <ChatShellContext.Provider value={value}>{children}</ChatShellContext.Provider>;
}

const ChatConnection = dynamic(() => import("./chat-connection"), { ssr: false });

class ChatErrorBoundary extends Component<{ children: ReactNode; onFail: () => void }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    Sentry.captureException(error, { tags: { boundary: "chat" } });
    phCaptureException(error, { boundary: "chat" });
    this.props.onFail();
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

export function ChatDock() {
  const shell = useChatShell();
  return shell ? (
    <ChatErrorBoundary key={`${shell.userId}:${shell.guest}`} onFail={() => shell.revoke("failed")}>
      <ChatConnection />
    </ChatErrorBoundary>
  ) : null;
}
